import { type ComponentProps } from 'react'

import { TBankCode } from '@/entities/account'
import { cn } from '@/shared/lib'
import { Button, Dialog } from '@/shared/ui'

type Props = {
  isOpen: boolean
  selectedBank?: string
  banks?: TBankCode[]
  onOpenChange: () => void
  onChange: (value: string) => void
}

export const BankDialog = ({
  isOpen,
  selectedBank,
  banks,
  onOpenChange,
  onChange,
}: Props) => {
  const isSelected = (bank: TBankCode) => bank.name === selectedBank

  const handleChange = (value: string) => () => {
    onChange(value)
  }

  return (
    <Dialog.Root open={isOpen} onOpenChange={onOpenChange}>
      <Dialog.Content className="max-h-[760px] gap-0 p-0">
        <Dialog.Header className="p-6">
          <Dialog.Title>은행을 선택해 주세요</Dialog.Title>
        </Dialog.Header>
        {(() => {
          if (!banks)
            return (
              <p className="typo-label1 flex px-6 font-medium text-gray-500">
                은행 목록을 불러오는 데 실패했습니다.
                <br />
                잠시 후 다시 시도해 주세요.
              </p>
            )

          return (
            <ul className="flex flex-wrap gap-2 px-6">
              {banks.map(bank => (
                <BankItem
                  key={bank.code}
                  selected={isSelected(bank)}
                  onClick={handleChange(bank.name)}
                  aria-selected={isSelected(bank)}
                >
                  {bank.name}
                </BankItem>
              ))}
            </ul>
          )
        })()}

        <Dialog.Footer className="p-6">
          <Button
            variant="solid"
            color="secondary"
            size="large"
            fullWidth
            onClick={onOpenChange}
          >
            닫기
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  )
}

type BankItemProps = ComponentProps<'button'> & {
  selected?: boolean
  ref?: (el: HTMLButtonElement | null) => void
}

const BankItem = ({ selected, children, ...props }: BankItemProps) => {
  return (
    <li>
      <button
        data-slot="select-item"
        className={cn(
          'typo-label1 flex h-11 w-[112px] cursor-pointer items-center justify-center rounded-sm bg-gray-50 px-1 py-2 font-medium text-black outline-hidden transition-colors',
          'focus-visible:ring-2 focus-visible:ring-gray-500/50',
          selected &&
            'border border-black bg-purple-50 focus-visible:ring-purple-500/50',
          !selected && 'hover:bg-gray-100',
        )}
        autoFocus={selected}
        {...props}
      >
        {children}
      </button>
    </li>
  )
}
